import Container from "../../common/Container";
import styled from "styled-components";
import appConfig from "../../config/appConfig";
import WaiverForm from "./WaiverForm";

const PdfEmbed = styled.object`
  display: block;
  width: 100%;
  height: 60vh;
  min-height: 400px;
  border-radius: 6px;
  border: 1px solid var(--color-border);
  margin-bottom: 1.5rem;
  /* iOS Safari can't paginate embedded PDFs — hide it on small screens */
  @media (max-width: 768px) {
    display: none;
  }
`;

const MobilePdfLink = styled.a`
  display: none;
  @media (max-width: 768px) {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    background: var(--color-surface);
    border: 1px solid var(--color-border);
    border-radius: 6px;
    padding: 0.9rem 1rem;
    margin-bottom: 1.5rem;
    text-decoration: none;
    color: var(--color-primary);
    font-weight: 500;
  }
`;

const GaggleLink = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 0.75rem;
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: 8px;
  padding: 0.75rem 1.1rem;
  text-decoration: none;
  color: inherit;
  font-weight: 500;
  margin-top: 0.25rem;
  &:hover {
    border-color: var(--color-primary);
  }
  img {
    width: 28px;
    height: 28px;
    flex-shrink: 0;
  }
`;

const rules = [
  "No low acro. We'll still watch you rip above 1,000 ft.",
  "Respect our neighbors: no flying near houses, especially in groups.",
  "Follow all instructions from event coordinators.",
];

function FlyIn() {
  const eventDate = appConfig.flyIn.eventDate
    ? new Date(appConfig.flyIn.eventDate + "T00:00").toLocaleDateString(
        undefined,
        { year: "numeric", month: "long", day: "numeric" }
      )
    : "TBA";

  const eventEndDate = appConfig.flyIn.eventEndDate
    ? new Date(appConfig.flyIn.eventEndDate + "T00:00").toLocaleDateString(
        undefined,
        { year: "numeric", month: "long", day: "numeric" }
      )
    : null;

  const eventDateDisplay = eventEndDate
    ? `${eventDate} – ${eventEndDate}`
    : eventDate;

  return (
    <Container>
      <h1>Robert Durbin Memorial Fly-In</h1>
      <p>
        Our annual gathering in memory of the pilot who built this community.
        Pilots and friends welcome — pilots must sign the waiver and pay the
        ${appConfig.flyIn.feeUsd} registration fee below.
      </p>
      <p>
        <strong>Location:</strong>{" "}
        <a
          href="https://maps.app.goo.gl/mAzRBxMGqt9MGjcAA"
          target="_blank"
          rel="noopener noreferrer"
        >
          West Middlesex Airport
        </a>
      </p>
      <p>
        <strong>Next event:</strong> {eventDateDisplay}
      </p>

      <h2>Rules</h2>
      <ul>
        {rules.map((r) => (
          <li key={r}>{r}</li>
        ))}
      </ul>

      <h2>Gaggle</h2>
      <p>
        Join our Gaggle group so we can keep an eye on each other.
      </p>
      <GaggleLink
        href="https://share.flygaggle.com/joinGroup?p=eyJncm91cElkIjoia1hFQy0xNzc5NTExMDc0MTM2Iiwiam9pbkNvZGUiOiJyTEU2NCJ9"
        target="_blank"
        rel="noopener noreferrer"
      >
        <img src="/gaggle-logo.svg" alt="Gaggle" />
        Join the fly-in Gaggle group
      </GaggleLink>

      <h2>Camping</h2>
      <p>
        Camping is available all along the fence, highlighted in green below. We limit camping to
        only the south half of the taxiway, so ultralights still have room to
        taxi to/from the runway.
      </p>
      <img
        src="/images/camping.webp"
        alt="Camping area map — green fence line and south half of taxiway"
        style={{ maxWidth: "100%", borderRadius: 8, margin: "0.5rem 0" }}
      />

      <h2>Site briefing</h2>
      <p>
         Please no low flying over the main Takeoff & Landing area. Watch out for powerlines and the electric fence. 
         If you want to fly by the field you're welcome to do so over the tall grass in green below.
      </p>
      <img
        src="/images/site-brief.webp"
        alt="West Middlesex Airport- PA21"
        style={{ maxWidth: "100%", borderRadius: 8, margin: "0.5rem 0" }}
      />

      <h2>Pilot waiver</h2>
      <p>
        All pilots attending the fly-in must read the full waiver below, then complete and sign the form. After
        signing you'll be sent to Venmo with the registration fee pre-filled.
        Include the confirmation ID we give you in the Venmo note so we can
        match your payment to your waiver.
      </p>
      <PdfEmbed
        data="/waiver.pdf"
        type="application/pdf"
        aria-label="Release of Liability, Assumption of Risk, and Indemnification Agreement"
      >
        <p>
          Your browser can't display PDFs.{" "}
          <a href="/waiver.pdf" target="_blank" rel="noopener noreferrer">
            Download the waiver PDF
          </a>{" "}
          and read it before signing below.
        </p>
      </PdfEmbed>
      <MobilePdfLink href="/waiver.pdf" target="_blank" rel="noopener noreferrer">
        📄 Read the full waiver PDF (opens in viewer)
      </MobilePdfLink>
      <WaiverForm />
    </Container>
  );
}

export default FlyIn;
